import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mic, BookOpen, Brain, Pencil as PencilIcon, Plus, Trash2, Check, Clock,
  AlertTriangle, X as XIcon, Sparkles, Filter, Layers, Trophy, Target,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { PageHeader } from "@/components/common/PageHeader";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Phase 13 — Prep Workspace (Layer 2 · Preparation)
// ---------------------------------------------------------------------------
// Four drill types render with the same card; type filter chip-row at top.
// Family role is read-only; admin gets full CRUD with markdown body editor.
// ---------------------------------------------------------------------------

const DRILL_TYPES = [
  { key: "whiteboard",    label: "Whiteboard",    icon: PencilIcon, tone: "cyan",   description: "Scheme & concept walk-throughs" },
  { key: "install",       label: "Install",       icon: BookOpen,   tone: "rose",   description: "Team-specific verbiage" },
  { key: "media",         label: "Media",         icon: Mic,        tone: "amber",  description: "Soundbites & hostile-Q drills" },
  { key: "psychological", label: "Mental",        icon: Brain,      tone: "violet", description: "Readiness, presence, regulation" },
];

const TYPE_BY_KEY = Object.fromEntries(DRILL_TYPES.map((t) => [t.key, t]));

const STATUS_TONE = {
  pending:     { tone: "neutral", label: "Pending"     },
  in_progress: { tone: "cyan",    label: "In progress" },
  complete:    { tone: "emerald", label: "Complete"    },
};

const PRIORITY_TONE = {
  low:    "ring-white/10",
  medium: "ring-amber-300/30",
  high:   "ring-rose-400/40",
};

const TYPE_TILE = {
  cyan:   "bg-cyan-300/10 ring-cyan-300/25 text-cyan-200",
  rose:   "bg-rose-400/10 ring-rose-400/25 text-rose-200",
  amber:  "bg-amber-300/10 ring-amber-300/25 text-amber-200",
  violet: "bg-violet-300/10 ring-violet-300/25 text-violet-200",
};

function emptyDrill() {
  return {
    drill_type: "whiteboard",
    title: "",
    summary: "",
    content_md: "",
    tags: [],
    coach_or_advisor: "",
    target_team: "",
    score: null,
    status: "pending",
    priority: "medium",
    due_at: "",
    visible_to_family: true,
  };
}

export default function PrepWorkspacePage() {
  useDocumentTitle("OmniDraftLab · Prep Workspace");
  const { user } = useAuth();
  const { playerId: paramPlayerId } = useParams();
  const isAdmin = user?.role === "admin";
  const playerId = paramPlayerId || user?.player_id;

  const [drills, setDrills] = useState([]);
  const [summary, setSummary] = useState({ by_type: {}, total: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const refresh = useCallback(async () => {
    if (!playerId) { setDrills([]); return; }
    const [r, s] = await Promise.all([
      api.get("/prep-drills", { params: { player_id: playerId } }),
      api.get("/prep-drills/summary", { params: { player_id: playerId } }),
    ]);
    setDrills(r.data || []);
    setSummary(s.data || { by_type: {}, total: 0, completed: 0 });
  }, [playerId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    refresh().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refresh]);

  const filtered = useMemo(() => {
    let xs = drills;
    if (filter !== "all") xs = xs.filter((d) => d.drill_type === filter);
    if (statusFilter !== "all") xs = xs.filter((d) => d.status === statusFilter);
    return xs;
  }, [drills, filter, statusFilter]);

  if (loading) return <LoadingState />;

  if (!playerId) {
    return <EmptyState icon={Layers} title="No athlete linked" description="Your account isn't linked to an athlete yet." />;
  }

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="prep-page">
      <PageHeader
        title="Prep Workspace"
        subtitle="Whiteboard, install verbiage, media drills, mental reps. Layer 2 — every room he'll walk into."
        actions={isAdmin && (
          <Button data-testid="prep-new" onClick={() => setEditing("new")} className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold rounded-xl">
            <Plus className="h-4 w-4 mr-1" /> New drill
          </Button>
        )}
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-2">
        {DRILL_TYPES.map((t) => {
          const s = summary.by_type?.[t.key] || { total: 0, complete: 0, avg_score: null };
          const Icon = t.icon;
          const ratio = s.total ? Math.round((s.complete / s.total) * 100) : 0;
          return (
            <button
              key={t.key}
              onClick={() => setFilter(filter === t.key ? "all" : t.key)}
              data-testid={`prep-summary-${t.key}`}
              className={cn(
                "glass p-4 text-left ring-1 transition",
                filter === t.key ? "ring-cyan-300/50" : "ring-white/10 hover:ring-cyan-300/30"
              )}
            >
              <div className="flex items-center justify-between">
                <div className={cn("h-9 w-9 rounded-lg grid place-items-center ring-1", TYPE_TILE[t.tone])}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-3 text-[10px] font-mono">{s.total}</span>
              </div>
              <div className="mt-3 text-1 text-sm font-semibold">{t.label}</div>
              <div className="mt-1 text-3 text-[11px] truncate">{t.description}</div>
              <div className="mt-3 flex items-center gap-2">
                <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-400 rounded-full transition-all" style={{ width: `${ratio}%` }} />
                </div>
                <span className="text-emerald-300 font-mono text-[11px]">{ratio}%</span>
              </div>
              {s.avg_score != null && (
                <div className="mt-2 text-3 text-[11px]">
                  Avg score · <span className="text-1 font-mono">{s.avg_score.toFixed(1)}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
        <Filter className="h-3.5 w-3.5 text-3" />
        <FilterPill active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testId="prep-fstatus-all">All status</FilterPill>
        {Object.entries(STATUS_TONE).map(([k, v]) => (
          <FilterPill key={k} active={statusFilter === k} onClick={() => setStatusFilter(k)} testId={`prep-fstatus-${k}`}>{v.label}</FilterPill>
        ))}
        <span className="ml-2 text-3">·</span>
        {filter !== "all" && (
          <button onClick={() => setFilter("all")} className="ml-1 inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200">
            <XIcon className="h-3 w-3" /> Clear type filter
          </button>
        )}
        <span className="ml-auto text-3 text-[11px]">
          {summary.completed} of {summary.total} complete
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            icon={Sparkles}
            title="No drills here yet"
            description={isAdmin ? "Add the first drill to start the prep cycle." : "Your advisor hasn't logged drills yet."}
          />
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-12 gap-4">
          {filtered.map((d, i) => (
            <DrillCard
              key={d.id}
              drill={d}
              index={i}
              isAdmin={isAdmin}
              onEdit={() => setEditing(d)}
              onDelete={async () => {
                if (!confirm(`Delete drill "${d.title}"?`)) return;
                await api.delete(`/prep-drills/${d.id}`);
                toast.success("Drill removed");
                await refresh();
              }}
              onMarkComplete={async () => {
                await api.patch(`/prep-drills/${d.id}`, { status: "complete" });
                toast.success("Marked complete");
                await refresh();
              }}
            />
          ))}
        </div>
      )}

      {editing && (
        <DrillDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          initial={editing === "new" ? emptyDrill() : editing}
          isNew={editing === "new"}
          playerId={playerId}
          onSaved={async (msg) => { toast.success(msg); setEditing(null); await refresh(); }}
        />
      )}
    </div>
  );
}

function FilterPill({ active, onClick, testId, children }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "px-2.5 py-1 rounded-full ring-1 transition",
        active ? "bg-cyan-300 text-[#04111A] ring-cyan-300 font-semibold" : "bg-white/[0.04] text-2 ring-white/10 hover:ring-cyan-300/30"
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Drill card
// ---------------------------------------------------------------------------
function DrillCard({ drill, index, isAdmin, onEdit, onDelete, onMarkComplete }) {
  const t = TYPE_BY_KEY[drill.drill_type] || DRILL_TYPES[0];
  const status = STATUS_TONE[drill.status] || STATUS_TONE.pending;
  const Icon = t.icon;
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.04, ease: [0.16, 1, 0.3, 1] }}
      className="col-span-12 md:col-span-6 lg:col-span-4"
      data-testid={`prep-card-${drill.id}`}
    >
      <GlassCard className={cn("p-5 h-full flex flex-col ring-1", PRIORITY_TONE[drill.priority])} hover>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className={cn("h-8 w-8 rounded-lg grid place-items-center ring-1 shrink-0", TYPE_TILE[t.tone])}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0">
              <div className="text-3 text-[10px] uppercase tracking-wider">{t.label}{drill.target_team && ` · ${drill.target_team}`}</div>
              <h3 className="text-1 text-sm font-semibold leading-tight truncate">{drill.title}</h3>
            </div>
          </div>
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </div>

        {drill.summary && <p className="mt-3 text-2 text-xs leading-snug">{drill.summary}</p>}

        {drill.content_md && (
          <div className="mt-3">
            {expanded ? (
              <div className="markdown-body text-2 text-xs prose prose-invert prose-headings:text-1 max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{drill.content_md}</ReactMarkdown>
              </div>
            ) : (
              <button onClick={() => setExpanded(true)} className="text-cyan-300 text-[11px] hover:text-cyan-200">
                Read full notes →
              </button>
            )}
          </div>
        )}

        {drill.tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {drill.tags.slice(0, 6).map((tag, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 ring-1 ring-white/10 text-2">{tag}</span>
            ))}
          </div>
        )}

        <div className="mt-3 flex items-center gap-3 text-3 text-[11px] flex-wrap">
          {drill.coach_or_advisor && <span className="inline-flex items-center gap-1"><Trophy className="h-3 w-3" /> {drill.coach_or_advisor}</span>}
          {drill.due_at && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(drill.due_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
          {drill.priority === "high" && <span className="inline-flex items-center gap-1 text-rose-300"><AlertTriangle className="h-3 w-3" /> High</span>}
          {drill.score != null && <span className="ml-auto text-1 font-mono">{drill.score.toFixed(1)}/10</span>}
        </div>

        {isAdmin && (
          <div className="mt-auto pt-4 flex items-center justify-end gap-1">
            {drill.status !== "complete" && (
              <Button data-testid={`prep-complete-${drill.id}`} onClick={onMarkComplete} variant="ghost" size="sm" className="text-emerald-300 hover:text-emerald-200 text-xs">
                <Check className="h-3.5 w-3.5 mr-1" /> Complete
              </Button>
            )}
            <Button data-testid={`prep-edit-${drill.id}`} onClick={onEdit} variant="ghost" size="icon" className="text-2 hover:text-white"><PencilIcon className="h-3.5 w-3.5" /></Button>
            <Button data-testid={`prep-delete-${drill.id}`} onClick={onDelete} variant="ghost" size="icon" className="text-2 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        )}
      </GlassCard>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit dialog
// ---------------------------------------------------------------------------
function DrillDialog({ open, onClose, initial, isNew, playerId, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(initial); }, [initial]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        score: form.score === "" || form.score == null ? null : Number(form.score),
        due_at: form.due_at || null,
      };
      if (isNew) {
        await api.post("/prep-drills", { ...payload, player_id: playerId });
        await onSaved("Drill added");
      } else {
        await api.patch(`/prep-drills/${form.id}`, payload);
        await onSaved("Drill updated");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass-strong border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prep-dialog">
        <DialogHeader>
          <DialogTitle className="text-1">{isNew ? "New drill" : "Edit drill"}</DialogTitle>
          <DialogDescription className="text-3 text-xs">Log a whiteboard, install, media, or mental drill. Markdown supported in the body.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-3 mt-2">
          <div className="col-span-12 md:col-span-4 space-y-1.5">
            <Label className="text-2 text-xs">Type</Label>
            <Select value={form.drill_type} onValueChange={(v) => set({ drill_type: v })}>
              <SelectTrigger data-testid="prep-form-type" className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {DRILL_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-12 md:col-span-8 space-y-1.5">
            <Label className="text-2 text-xs">Title *</Label>
            <Input data-testid="prep-form-title" value={form.title || ""} onChange={(e) => set({ title: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-12 space-y-1.5">
            <Label className="text-2 text-xs">Summary</Label>
            <Input value={form.summary || ""} onChange={(e) => set({ summary: e.target.value })} className="bg-white/5 border-white/10" placeholder="One-line description" />
          </div>
          <div className="col-span-6 md:col-span-3 space-y-1.5">
            <Label className="text-2 text-xs">Status</Label>
            <Select value={form.status} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {Object.entries(STATUS_TONE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 md:col-span-3 space-y-1.5">
            <Label className="text-2 text-xs">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set({ priority: v })}>
              <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {["low", "medium", "high"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 md:col-span-3 space-y-1.5">
            <Label className="text-2 text-xs">Target team</Label>
            <Input value={form.target_team || ""} onChange={(e) => set({ target_team: e.target.value })} className="bg-white/5 border-white/10" placeholder="e.g. Bears" />
          </div>
          <div className="col-span-6 md:col-span-3 space-y-1.5">
            <Label className="text-2 text-xs">Coach / Advisor</Label>
            <Input value={form.coach_or_advisor || ""} onChange={(e) => set({ coach_or_advisor: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-12 md:col-span-6 space-y-1.5">
            <Label className="text-2 text-xs">Tags (comma-separated)</Label>
            <Input
              value={(form.tags || []).join(", ")}
              onChange={(e) => set({ tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              className="bg-white/5 border-white/10"
              placeholder="cover-2, hot-route, hostile-q"
            />
          </div>
          <div className="col-span-12 md:col-span-6 space-y-1.5">
            <Label className="text-2 text-xs">Due date</Label>
            <Input type="datetime-local" value={form.due_at ? form.due_at.slice(0, 16) : ""} onChange={(e) => set({ due_at: e.target.value ? new Date(e.target.value).toISOString() : null })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-12 space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-2 text-xs">Score · {form.score != null ? Number(form.score).toFixed(1) : "—"}</Label>
              {form.score != null && (
                <button onClick={() => set({ score: null })} className="text-3 text-[11px] hover:text-rose-300">Clear</button>
              )}
            </div>
            <Slider min={1} max={10} step={0.5} value={[form.score ?? 1]} onValueChange={(arr) => set({ score: arr[0] })} />
          </div>
          <div className="col-span-12 space-y-1.5">
            <Label className="text-2 text-xs">Notes (markdown)</Label>
            <Textarea
              data-testid="prep-form-content"
              rows={6}
              value={form.content_md || ""}
              onChange={(e) => set({ content_md: e.target.value })}
              className="bg-white/5 border-white/10 text-1 font-mono text-sm"
              placeholder="### Concept&#10;Explain the install, the cue, the answer.&#10;&#10;- bullet&#10;- bullet"
            />
          </div>
        </div>

        <DialogFooter className="mt-3 gap-2">
          <Button variant="ghost" onClick={onClose} className="text-2">Cancel</Button>
          <Button data-testid="prep-save" onClick={save} disabled={busy} className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold">
            <Check className="h-4 w-4 mr-1" /> {isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
